import React, { useEffect, useState } from 'react';

import clientServerBridge from '@helpcenter/client-server-bridge';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { operationalEvent } from '@atlassian/help-center-common-util/analytics/events';
import type { CmdbObject } from '../../rest/cmdb-object-picker';
import { MemoizedInsightIcon } from '../cmdb-object-picker/insight-icon';
import { AttributeList } from './attribute-list';
import { ExpandCollapseToggle } from './expand-collapse-toggle';
import messages from './messages';

export interface Props {
	cmdbObject: CmdbObject | null;
	/** When true, hides the expand/collapse chevron. */
	hideExpand?: boolean;
	/** When true, always renders the expand/collapse chevron even if the object has no attributes. Ignored when hideExpand is true. */
	alwaysShowExpand?: boolean;
	/** Called the first time the card is expanded. Receives the object's global id. */
	onExpand?: (objectGlobalId: string) => void;
	/** When true, shows a loading spinner in the expanded section while attributes are fetched. */
	isLoadingAttributes?: boolean;
}

const CmdbObjectCard = ({
	cmdbObject,
	onExpand,
	intl,
	hideExpand = false,
	alwaysShowExpand = false,
	isLoadingAttributes = false,
}: Props & WrappedComponentProps) => {
	const [expanded, setExpanded] = useState(false);
	const [hasRequestedAttributes, setHasRequestedAttributes] = useState(false);

	const handleToggleExpand = () => {
		const next = !expanded;
		setExpanded(next);
		if (
			next &&
			!hasRequestedAttributes &&
			onExpand &&
			cmdbObject &&
			FeatureGates.checkGate('assets_custom_fields_limit_increase_ga')
		) {
			setHasRequestedAttributes(true);
			onExpand(cmdbObject.id);
		}
	};

	const timezoneId =
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		typeof document !== 'undefined'
			? clientServerBridge.querySelector('meta[name="user-timezone"]')?.getAttribute('content')
			: undefined;

	useEffect(() => {
		// Fire an event when the component is rendered, to measure successful renders
		if (cmdbObject !== null) {
			operationalEvent({
				action: 'rendered',
				actionSubject: 'cmdbObjectCard',
				source: 'cmdbObjectCard',
			});
		}
	}, [cmdbObject]);

	if (cmdbObject === null) {
		return intl.formatMessage(messages.error);
	}

	const renderChevronButton = () => {
		if (hideExpand || (cmdbObject.attributes.length === 0 && !alwaysShowExpand)) {
			return null;
		}
		const buttonLabel = intl.formatMessage(
			expanded ? messages.collapseLabel : messages.expandLabel,
		);
		return (
			<ExpandCollapseToggle
				isExpanded={expanded}
				label={buttonLabel}
				onClick={handleToggleExpand}
				testId="cmdb-object-card-expand-collapse-toggle"
			/>
		);
	};

	const containerVariantStyles = [
		containerStyles,
		...(hideExpand || alwaysShowExpand ? [newExperienceCardStyles] : []),
		...(hideExpand ? [reserveRemoveSpaceStyles] : []),
	];

	return (
		<Box xcss={containerVariantStyles} testId="cmdb-object-card">
			<Inline alignBlock="center">
				<MemoizedInsightIcon
					{...cmdbObject.avatar.mediaClientConfig}
					iconUrl={cmdbObject.avatar.url48}
				/>
				<Box xcss={labelStyles}>{cmdbObject.label}</Box>
				{renderChevronButton()}
			</Inline>
			{expanded && (
				<Box xcss={attributesListContainerStyles}>
					{FeatureGates.checkGate('assets_custom_fields_limit_increase_ga') &&
						isLoadingAttributes && (
							<Box xcss={loadingStyles}>
								<Spinner size="small" label={intl.formatMessage(messages.loadingAttributes)} />
							</Box>
						)}
					{(!FeatureGates.checkGate('assets_custom_fields_limit_increase_ga') ||
						!isLoadingAttributes) &&
						cmdbObject.attributes.length > 0 && (
							<AttributeList attributes={cmdbObject.attributes} timezoneId={timezoneId} />
						)}
					{FeatureGates.checkGate('assets_custom_fields_limit_increase_ga') &&
						!isLoadingAttributes &&
						cmdbObject.attributes.length === 0 && (
							<Box xcss={noAttributesStyles} role="status" aria-live="polite">
								{intl.formatMessage(messages.noAttributes)}
							</Box>
						)}
				</Box>
			)}
		</Box>
	);
};
export default injectIntl(CmdbObjectCard);

const containerStyles = xcss({
	position: 'relative',
	width: '100%',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	marginBottom: 'space.100',
	padding: 'space.100',
	boxSizing: 'border-box',
});

const newExperienceCardStyles = xcss({
	marginBottom: 'space.025',
	borderRadius: 'radius.medium',
});

const reserveRemoveSpaceStyles = xcss({
	maxWidth: '100%',
	paddingRight: 'space.400',
});

const labelStyles = xcss({
	font: 'font.body',
	color: 'color.text.subtle',
	margin: 'space.0',
	paddingLeft: 'space.100',
	flexGrow: 1,
	minWidth: 'space.0',
	overflowWrap: 'break-word',
});

const attributesListContainerStyles = xcss({
	marginTop: 'space.200',
});

const noAttributesStyles = xcss({
	font: 'font.body.small',
	color: 'color.text.subtlest',
});

const loadingStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	paddingBlock: 'space.100',
});
